import type { Session } from '@sentra/contracts';
import encodeQR from 'qr';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiRoutes } from '@/api/routes';
import { useApiErrorMessage } from '@/api/use-api-error';
import { Alert, AlertDescription } from '@/design/components/alert';
import { Button } from '@/design/components/button';
import { Input } from '@/design/components/input';
import { Label } from '@/design/components/label';
import { AuthCard } from '@/features/auth/auth-card';
import { useSessionContext } from '@/features/auth/session';

/**
 * Enrolling an authenticator, and the recovery codes that come with it.
 *
 * **The secret and the recovery codes appear here and in no other place.** Neither is retrievable
 * afterwards: the enrolment offer is minted per request, and the codes are stored only as hashes.
 * The acknowledgment step exists because of that — a screen that vanished on the next navigation
 * would take ten single-use codes with it, and the person who loses their phone is the one who
 * finds out.
 *
 * **The QR is rendered locally, from the `otpauth://` URI, as an SVG.** Section 1.10's
 * `img-src 'self'` and `connect-src 'self'` refuse a remote chart service outright, and sending a
 * TOTP secret to one would be handing somebody else the second factor. `qr` is the same encoder the
 * server renders a pass with, so there is one implementation of this in the product.
 *
 * **The session in the confirmation response replaces the one held**, for the reason the password
 * change does: confirming rotates the session id and the token bound to the old one is dead.
 */
export function TotpEnrolmentPage(): ReactNode {
  const { t } = useTranslation();
  const { client, adopt } = useSessionContext();
  const describe = useApiErrorMessage();

  const [offer, setOffer] = useState<{ secret: string; uri: string } | null>(null);
  const [token, setToken] = useState('');
  const [codes, setCodes] = useState<readonly string[] | null>(null);
  const [pending, setPending] = useState<Session | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let live = true;
    void client
      .request(apiRoutes.authTotpBegin, { body: {} })
      .then((issued) => {
        if (live) {
          setOffer(issued);
        }
      })
      .catch((error: unknown) => {
        if (live) {
          setFailure(describe(error));
        }
      });
    return () => {
      live = false;
    };
  }, [client, describe]);

  async function confirm(): Promise<void> {
    setFailure(null);
    setSubmitting(true);
    try {
      const confirmed = await client.request(apiRoutes.authTotpConfirm, { body: { token } });
      setCodes(confirmed.recoveryCodes);
      setPending(confirmed.session);
    } catch (error) {
      setFailure(describe(error));
    } finally {
      setSubmitting(false);
    }
  }

  if (codes !== null && pending !== null) {
    return (
      <AuthCard title={t('auth.recoveryCodesTitle')} description={t('auth.recoveryCodesBody')}>
        <ul className="grid grid-cols-2 gap-2 font-mono text-sm" data-testid="recovery-codes">
          {codes.map((code) => (
            <li key={code} className="rounded-md border bg-muted px-3 py-2">
              {code}
            </li>
          ))}
        </ul>
        <Button
          className="mt-6 w-full"
          onClick={() => {
            adopt(pending);
          }}
        >
          {t('auth.recoveryCodesAcknowledge')}
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('auth.enrolTitle')} description={t('auth.enrolSubtitle')}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void confirm();
        }}
        noValidate
      >
        {offer !== null && (
          <>
            <div
              role="img"
              aria-label={t('auth.enrolQrLabel')}
              data-testid="totp-qr"
              className="mx-auto w-48 [&>svg]:h-auto [&>svg]:w-full"
              // The SVG is produced here, in this process, from a value this process was handed.
              // Nothing user-supplied reaches it: the URI is the server's own enrolment offer and
              // the encoder emits a fixed grammar of `<path>` elements.
              dangerouslySetInnerHTML={{ __html: encodeQR(offer.uri, 'svg') }}
            />
            <div className="flex flex-col gap-2">
              <Label htmlFor="totp-secret">{t('auth.enrolSecret')}</Label>
              <Input id="totp-secret" readOnly value={offer.secret} className="font-mono" />
            </div>
          </>
        )}

        <div className="flex flex-col gap-2">
          <Label htmlFor="totp-token">{t('auth.totpCode')}</Label>
          <Input
            id="totp-token"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={token}
            onChange={(event) => {
              setToken(event.target.value);
            }}
          />
        </div>

        {failure !== null && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{failure}</AlertDescription>
          </Alert>
        )}

        <Button type="submit" disabled={submitting || offer === null}>
          {t('auth.enrolConfirm')}
        </Button>
      </form>
    </AuthCard>
  );
}
